import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSystemAnnouncements } from '../../contexts/SystemAnnouncementsContext';

export const announcementPanel = 'rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';
export const announcementButton = 'rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700';

export function AnnouncementAudience({ item }) {
  const { t } = useTranslation();
  const value = useSystemAnnouncements();
  return <span className="break-words text-sm">{item.entity_id == null
    ? t('announcements.global') : t('announcements.entityAudience', { entity: value?.entityName })}</span>;
}

export default function AnnouncementInbox({ canManage = false, canManageEntity = false }) {
  const value = useSystemAnnouncements();
  const { announcementId } = useParams();
  const { t } = useTranslation();
  const store = value?.store;
  useEffect(() => {
    if (!store || !value.enabled) return;
    if (announcementId && /^[1-9]\d*$/.test(announcementId)) void store.open(Number(announcementId));
    else { store.closeDetail(); void store.refresh(); }
    return () => store.closeDetail();
  // A new audience or acknowledged setting starts a new empty projection.
  // Reopen the URL through that audience, without moving the user's selection.
  }, [store, value?.enabled, value?.projectionVersion, announcementId]);
  if (!value?.enabled) return null;
  const { items, loaded, loading, error, detail, detailLoading, detailUnavailable, actionPending, nextCursor } = value;
  const refresh = async () => {
    if (!announcementId) return store.refresh({ force: true });
    // A disabled feed retired entity detail authority. Refresh that authority
    // before retrying an unavailable deep link, so a remote re-enable can be
    // observed by this explicit action without waiting for navigation/focus.
    if (value.entityAnnouncementsEnabled === false && !detail) await store.refresh({ force: true });
    // Keep a displayed message through a transient refresh failure. A retry of
    // an initial failed load still acknowledges its first successful display.
    const displayed = detail?.id === Number(announcementId);
    return store.open(Number(announcementId), { retain: true, markRead: !displayed });
  };
  return (
    <main className="mx-auto w-full max-w-4xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('announcements.title')}</h1>
        <div className="flex flex-wrap gap-2">
          {canManage && <Link className={announcementButton} to="/system-announcements">{t('announcements.manageGlobal')}</Link>}
          {canManageEntity && value.scope?.type === 'entity' && <Link className={announcementButton} to="/entity-announcements">{t('announcements.manageEntity')}</Link>}
          <button className={announcementButton} type="button" disabled={loading || actionPending} onClick={refresh}>{t('announcements.refresh')}</button>
        </div>
      </div>
      {value.scope?.type === 'global' && <p className="text-sm">{t('announcements.allScopeHelp')}</p>}
      {error && <p role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-950">{t(error === 'action' ? 'announcements.actionError' : 'announcements.loadError')}</p>}
      {announcementId ? <>
        <Link to="/announcements" className="inline-block text-blue-600 underline dark:text-blue-300">{t('announcements.back')}</Link>
        {detailLoading && <p role="status">{t('announcements.loading')}</p>}
        {(detailUnavailable || !/^[1-9]\d*$/.test(announcementId)) && <p role="status">{t('announcements.unavailable')}</p>}
        {detail?.id === Number(announcementId) && <article className={announcementPanel}>
          <AnnouncementAudience item={detail} />
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">{t(`announcements.severity.${detail.severity}`)}</p>
          <h2 className="mb-4 break-words text-xl font-semibold">{detail.title}</h2>
          <p className="whitespace-pre-wrap break-words">{detail.body}</p>
          {!detail.read_at && <button className={`${announcementButton} mt-4`} disabled={actionPending} onClick={() => store.acknowledge(detail.id, false)}>{t('announcements.markRead')}</button>}
        </article>}
      </> : <>
        {loading && <p role="status">{t('announcements.loading')}</p>}
        {loaded && items.length === 0 && <p>{t('announcements.empty')}</p>}
        <ul className="space-y-3">
          {items.map(item => <li key={item.id} className={announcementPanel}>
            <AnnouncementAudience item={item} />
            <div className="flex flex-wrap items-start justify-between gap-3">
              <Link to={`/announcements/${item.id}`} className="min-w-0 flex-1 break-words text-lg font-semibold text-blue-700 underline dark:text-blue-300">{item.title}</Link>
              {!item.read_at && <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-900">{t('announcements.unread')}</span>}
            </div>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300">{item.body.length > 240 ? `${item.body.slice(0, 240)}…` : item.body}</p>
            {!item.read_at && <button type="button" className={`${announcementButton} mt-3`} disabled={actionPending} onClick={() => store.acknowledge(item.id, false)}>{t('announcements.markRead')}</button>}
          </li>)}
        </ul>
        {nextCursor && <button type="button" className={announcementButton} disabled={loading || actionPending} onClick={() => store.refresh({ cursor: nextCursor })}>{t('announcements.loadMore')}</button>}
      </>}
    </main>
  );
}
